'use client';

import {
  useCallback,
  useMemo,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useTargetType } from '../../../store/projectStore';
import {
  useMainContentTabs,
  type OpenMainContentTabInput,
} from '../../../store/layoutStore';
import { useGlobalContextMenu } from '../../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../../store/globalContextMenuStore';
import {
  buildSignalRelatedAssetTab,
  buildSignalTab,
} from './signal-tabs.model';

export type SignalsTabOpenEvent = {
  event: MouseEvent | ReactMouseEvent<HTMLElement>;
  tab: OpenMainContentTabInput | undefined;
};

export function useSignalsNavigation({
  isRejectedView,
}: {
  isRejectedView: boolean;
}) {
  const tSignals = useTranslations('signals');
  const targetType = useTargetType();
  const { navigateInActiveTab, openInNewTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const getSignalTab = useCallback(
    (signal: GroupedSignal) =>
      buildSignalTab({
        signal,
        isRejectedView,
        rejectedTitle: tSignals('rejectedSignalDetailsTitle'),
      }),
    [isRejectedView, tSignals],
  );
  const getRelatedAssetTab = useCallback(
    (signal: GroupedSignal) =>
      buildSignalRelatedAssetTab({ signal, targetType }),
    [targetType],
  );
  const openTab = useCallback(
    (tab: OpenMainContentTabInput | undefined) => {
      if (tab) {
        navigateInActiveTab(tab);
      }
    },
    [navigateInActiveTab],
  );
  const openFullPageTab = useCallback(
    (tab: OpenMainContentTabInput | undefined) => {
      if (tab) {
        openInNewTab(tab);
      }
    },
    [openInNewTab],
  );
  const openTabContextMenu = useCallback(
    ({ event, tab }: SignalsTabOpenEvent) => {
      if (!tab) return;

      event.stopPropagation();
      openContextMenu({
        event,
        items: [{ type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB, tab }],
      });
    },
    [openContextMenu],
  );

  return useMemo(
    () => ({
      getSignalTab,
      getRelatedAssetTab,
      openTab,
      openFullPageTab,
      openTabContextMenu,
    }),
    [
      getRelatedAssetTab,
      getSignalTab,
      openFullPageTab,
      openTab,
      openTabContextMenu,
    ],
  );
}

export type SignalsNavigation = ReturnType<typeof useSignalsNavigation>;
